/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useCallback, useEffect, useState, useRef } from 'react';
import styles from './CustomerDetailsGeneral.module.scss';
import { Field } from '@progress/kendo-react-form';
import {
    FormDropDown,
    FormInput,
    LookupTextFormField,
    optionalUrlValidator,
    requiredValidator,
    TextFormField,
    useConfirmation,
} from '../../../common';
import { IAppSettings, ICustomer, ILookupValue } from '../../../../model';
import { ImageViewField } from '../../Common/ImageViewField';
import { CheckBoxFormField } from '../../../common/forms/form-checkbox/CheckBoxFormField';
import { ExternalURLTextFormField } from '../../../common/forms/text-form-field/ExternalURLTextFormField';
import { Button } from "@progress/kendo-react-buttons";
import { useGleap } from "../../../../hooks/useGleap";
import { customersService, notifications } from "../../../../services";
import { useAppSelector } from "../../../../store/hooks";
import { selectUserProfile } from "../../../../features";
import Gleap from "gleap";
import {ICustomerSnapshot} from "../../../../model/api/customers/customer-snapshot";

interface Props {
    edit: boolean;
    industrySectors: ILookupValue[];
    customer: ICustomer;
    valueGetter: (name: string) => any;
    formRenderProps?: any;
    appSettings?: IAppSettings | null;
    isNew?: boolean | undefined;
    hasSnapshotAccess?: boolean | undefined;
}

enum SnapshotGleapTicketStatus {
    DONE = 'DONE',
    OPEN = 'OPEN',
    INPROGRESS = 'INPROGRESS',
    NOT_FOUND = '404'
}

const GLEAP_TICKET_URL = 'https://app.gleap.io/projects/68279cd96589975af8bc189a/ops-requests';

const CustomerDetailsGeneral = (props: Props) => {
    const {
        edit,
        formRenderProps,
        customer,
        industrySectors,
        appSettings,
        hasSnapshotAccess
    } = props;

    const confirmation = useConfirmation();
    const { startSnapshotForm, getTicketsByUserId, getTicketInfoById } = useGleap();
    const [isSyncingWithGleap, setIsSyncingWithGleap] = useState(false);
    const [isGleapOverlay, setIsGleapOverlay] = useState(false);
    const userProfile = useAppSelector(selectUserProfile) as { username: string };

    const [ticketStatus, setTicketStatus] = useState<SnapshotGleapTicketStatus | null>(null);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);

    const currentTicketId = formRenderProps.valueGetter('ticketId');
    const currentSnapshotsEnabled = formRenderProps.valueGetter('snapshotsEnabled');

    const isAwaitingTicket = useRef(false);
    const formPropsRef = useRef(formRenderProps);
    formPropsRef.current = formRenderProps;
    const customerIdRef = useRef(customer.id);
    customerIdRef.current = customer.id;

    useEffect(() => {
        if (currentTicketId) {
            setIsLoadingStatus(true);
            getTicketInfoById(currentTicketId)
                .then((ticketInfo) => {
                    if (ticketInfo?.error && ticketInfo?.error?.statusCode === 404) {
                        setTicketStatus(SnapshotGleapTicketStatus.NOT_FOUND);
                        return
                    }
                    if (ticketInfo) {
                        setTicketStatus(ticketInfo.status);
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setIsLoadingStatus(false));
        } else {
            setTicketStatus(null);
        }
    }, [currentTicketId, getTicketInfoById]);

    useEffect(() => {
        const handleClose = () => {
            setIsGleapOverlay(false);
        };

        Gleap.on("close", handleClose);
        return () => {};
    }, []);

    useEffect(() => {
        let isComponentMounted = true;

        const handleFeedback = async (data: any) => {
            const startTime = performance.now();
            console.log(`[Gleap Sync] Event "feedback-sent" received at ${new Date().toISOString()}`);
            console.log(`[Gleap Sync] Context: customerId=${customerIdRef.current}, currentTicketId=${currentTicketId}`);

            if (!isAwaitingTicket.current) {
                console.log('[Gleap Sync] No pending Gleap ticket sync, ignoring feedback-sent event.');
                return;
            }

            if (!isComponentMounted) {
                console.warn('[Gleap Sync] Component unmounted, aborting sync.');
                return;
            }
            setIsGleapOverlay(true);
            const maxAttempts = 10;
            const delayMs = 3000;

            const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                if (!isComponentMounted) {
                    console.warn(`[Gleap Sync] Component unmounted during attempt ${attempt}.`);
                    return;
                }
                try {
                    setIsSyncingWithGleap(true);
                    const requestStartTime = performance.now();

                    const result: any = await getTicketsByUserId(userProfile?.username || '');
                    console.log(`[Gleap Sync] Attempt ${attempt}: Fetching tickets for user: ${userProfile?.username}...`);

                    const requestDuration = ((performance.now() - requestStartTime) / 1000).toFixed(2);
                    console.log(`[Gleap Sync] Attempt ${attempt}: API responded in ${requestDuration}s.`, result);

                    const tickets = result?.tickets || [];
                    const latestTicket = tickets.length > 0 ? tickets[tickets.length - 1] : null;

                    if (latestTicket) {
                        console.log(`[Gleap Sync] Found latest ticket: ID=${latestTicket.id}. Comparing with currentTicketId=${currentTicketId}`);

                        if (latestTicket.id !== currentTicketId) {
                            console.log(`[Gleap Sync] MATCH! Linking ticket ${latestTicket.id} to customer ${customerIdRef.current}`);

                            setTimeout(() => {
                                formPropsRef.current.onChange('ticketId', { value: latestTicket.id });
                            }, 0);

                            const dbUpdateStart = performance.now();
                            await customersService.updateSnapshotTicket(customerIdRef.current, latestTicket.id);
                            const dbUpdateDuration = ((performance.now() - dbUpdateStart) / 1000).toFixed(2);

                            console.log(`[Gleap Sync] DB updated successfully in ${dbUpdateDuration}s.`);

                            if (isComponentMounted) {
                                notifications.success('Snapshot service ticket created successfully.');
                                setIsGleapOverlay(false);
                            }

                            isAwaitingTicket.current = false;
                            const totalDuration = ((performance.now() - startTime) / 1000).toFixed(2);
                            console.log(`[Gleap Sync] SUCCESS! Total sync time: ${totalDuration}s.`);
                            return;
                        } else {
                            console.log(`[Gleap Sync] Latest ticket ID matches current one. Still waiting for the new ticket to appear in Gleap API...`);
                        }
                    } else {
                        console.log(`[Gleap Sync] No tickets found for this user yet.`);
                    }
                } catch (err) {
                    console.error(`Attempt ${attempt} failed:`, err);
                } finally {
                    if (isComponentMounted)  {
                        setIsGleapOverlay(false);
                        setIsSyncingWithGleap(false);
                    }
                }

                if (attempt < maxAttempts) {
                    console.log(`[Gleap Sync] Waiting ${delayMs}ms before next attempt...`);
                    await sleep(delayMs);
                }
            }

            if (isComponentMounted) {
                setIsGleapOverlay(false);
                isAwaitingTicket.current = false;
                console.error(`[Gleap Sync] FAILED: Ticket not found after ${maxAttempts} attempts. Total time: ${((performance.now() - startTime) / 1000).toFixed(2)}s.`);
            }
        };
        Gleap.on("feedback-sent", handleFeedback);

        return () => {
            isComponentMounted = false;
        };
    }, [userProfile?.username, currentTicketId, getTicketsByUserId]);

    const handleDeactivate = useCallback(
        async (event: any, onChange: (name: string, options: { value: any }) => void) => {
            const newValue = event.value;
            if (newValue) {
                return;
            }
            confirmation({
                title: 'Confirm deactivation of this Customer?',
                message:
                    'Please note that deactivating this item will not automatically disable associated services or devices such as cameras. These actions must be completed manually in the portal.',
            })
                .then(() => {
                    onChange('enabled', { value: false });
                })
                .catch((err) => {
                    onChange('enabled', { value: true });
                });
        },
        [confirmation],
    );

    const createRequestTicket = () => {
        setIsGleapOverlay(true);
        startSnapshotForm(customer?.name || 'undefined').then((success) => {
            if (success) {
                isAwaitingTicket.current = true;
            } else {
                setIsGleapOverlay(false);
            }
        });
    }

    const getSnapshotTicketButtonText = () => {
        if (isLoadingStatus) return 'Loading...';

        switch (ticketStatus) {
            case SnapshotGleapTicketStatus.DONE:
                return 'Enabled';
             case SnapshotGleapTicketStatus.OPEN:
                 return 'Open';
             case SnapshotGleapTicketStatus.INPROGRESS:
                return 'Processing';
             case SnapshotGleapTicketStatus.NOT_FOUND:
                return 'Ticket not found';
             default:
                return 'Unknown Status';
        }
    };

    const SaveSnapshot = async () => {
        if (!customer?.id) return;

        try {
            const payload = {
                snapshotsEnabled: formRenderProps.valueGetter('snapshotsEnabled'),
                snapshotsBucket: formRenderProps.valueGetter('snapshotsBucket'),
                snapshotsBucketDownload: formRenderProps.valueGetter('snapshotsBucketDownload')
            } as ICustomerSnapshot;

            await customersService.updateSnapshot(customer.id, payload);

            notifications.success('Snapshot settings have been successfully updated.');
        } catch (error) {
            notifications.error('Failed to update snapshot settings. Please try again.');
        }
    };

    return (
        <>
            {isGleapOverlay && (<div className={styles.globalLoaderOverlay}>
                    {isSyncingWithGleap && <span className="k-icon k-i-loading k-font-icon" style={{ fontSize: '48px' }}></span>}
            </div>)}

        <div className={styles.panelBar}>
            <div className={styles.panel}>
                <div className={styles.leftPanel}>
                    <Field
                        name={'name'}
                        label={'Name'}
                        required={true}
                        validator={requiredValidator}
                        maxLength={256}
                        component={edit ? FormInput : TextFormField}
                    />
                    {!edit && (
                        <>
                            <label htmlFor="customerId">Customer ID</label>
                            <div id={'customerId'} className="k-form-field-wrap">{customer.uniqueId}</div>
                        </>
                    )}
                    <Field
                        name={'websiteUrl'}
                        label={'Website URL'}
                        validator={optionalUrlValidator}
                        maxLength={2048}
                        type='url'
                        target={'_blank'}
                        component={edit ? FormInput : ExternalURLTextFormField}
                    />
                    <Field
                        name={'industrySector'}
                        label={'Industry Sector'}
                        data={industrySectors}
                        dataItemKey='id'
                        textField='name'
                        component={edit ? FormDropDown : LookupTextFormField}
                    />
                </div>

                <div className={styles.rightPanel}>
                    {!edit && formRenderProps.valueGetter('imageId') === undefined || formRenderProps.valueGetter('imageId') === null
              ?
              null
              :<Field
                        name={'logo'}
                        label={'Logo'}
                        edit={edit}
                        src={
                            formRenderProps.valueGetter('imageId') !== undefined
                                ? `${appSettings?.api_url}/image/${formRenderProps.valueGetter('imageId')}`
                                : undefined
                        }
                        component={ImageViewField}
                    />}

                    <Field
                        name={'enabled'}
                        label={'Status'}
                        edit={edit}
                        secondLabel={'Active'}
                        component={CheckBoxFormField}
                        onChange={edit ? (event) => handleDeactivate(event, formRenderProps.onChange) : void 0}
                    />

                    {hasSnapshotAccess ?
                        <>
                            <Field
                                name={'snapshotsEnabled'}
                                label={'Snapshot Service Settings'}
                                secondLabel={'Enabled'}
                                edit={true}
                                component={CheckBoxFormField} />
                            <Field
                                name={'snapshotsBucket'}
                                label={'Snapshot Bucket Name'}
                                required={!!currentSnapshotsEnabled}
                                maxLength={256}
                                component={FormInput}
                                disabled={!currentSnapshotsEnabled}
                            />
                            <Field
                                name={'snapshotsBucketDownload'}
                                label={'Snapshot Bucket Download Name'}
                                maxLength={256}
                                component={FormInput}
                                disabled={!currentSnapshotsEnabled}
                            />
                            <Button themeColor='primary' type='button' onClick={() => SaveSnapshot()}>
                                Save Snapshot Settings
                            </Button>
                        </>
                        : customer.id ?
                            <div className={styles.snapshotTicketCard}>
                                <div className={styles.snapshotTicketCardTitle}>Camera Settings</div>
                                {!currentTicketId || ticketStatus === SnapshotGleapTicketStatus.NOT_FOUND ?
                                    <>
                                        <div className={styles.snapshotTicketCardSubTitle}>
                                            Manage customer settings and camera configuration
                                        </div>
                                        <div className={styles.snapshotTicketCardInfoBlock}>
                                            <span>Enable&nbsp;<b>Camera Portal</b>&nbsp;and&nbsp;<b>Snapshot Service</b>&nbsp;for this customer by submitting a request.</span>
                                            <span>The onboarding includes several internal setup steps and is typically completed within 48 hours after the request is reviewed.</span>
                                            <span>You will receive a notification once the setup is fully completed.</span>
                                            <span>
                                        <a target="_blank" rel="noreferrer" href="https://umojo.atlassian.net/wiki/x/AwCCKg">Learn more about the onboarding process.</a>
                                    </span>
                                        </div>
                                    </>
                                    : null  }

                                {currentTicketId && currentSnapshotsEnabled ?
                                    <Button className={styles.snapshotEnabledBtn} disabled={true}>
                                        Enabled
                                    </Button>
                                    : !currentTicketId || ticketStatus === SnapshotGleapTicketStatus.NOT_FOUND
                                        ?
                                        <Button className={styles.snapshotTicketCardMainButton}
                                                                    type={"button"}
                                                                    disabled={!userProfile?.username}
                                                                    onClick={createRequestTicket}>
                                            Request to enable camera onboarding flow
                                        </Button>
                                            :
                                        <Button className={styles.snapshotPendingBtn} disabled={true}>
                                            {getSnapshotTicketButtonText()}
                                        </Button>
                                }
                            </div> : null
                    }

                    {currentTicketId && ticketStatus !== SnapshotGleapTicketStatus.NOT_FOUND &&
                        <>
                            <div className={styles.snapshotTicketLabelWrapper}>
                                <span className={styles.snapshotTicketLabel}>TICKET ID: </span>
                                <a href={`${GLEAP_TICKET_URL}/${currentTicketId}`} target={"_blank"} rel="noreferrer"
                                   className={styles.snapshotTicketNumber}>{currentTicketId}</a>
                            </div>
                            {customer.snapshotsBucket && customer.snapshotsBucketDownload && ticketStatus !== SnapshotGleapTicketStatus.DONE &&
                                <div className={styles.snapshotSnippet}>
                                    <span className={styles.snapshotTicketLabel}>Snapshot Bucket Name and Bucket Download already configured.</span>
                                    <span className={styles.snapshotTicketLabel}>Please ask developer operations for the details. </span>
                                </div>
                            }
                        </>

                    }


                </div>
            </div>
        </div>
        </>
    );
};
export default CustomerDetailsGeneral;
